import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildIndividualReturn } from '../../../../../utils/individualOpenMode';
import { INDIVIDUAL_TECHNOLOGY_RADAR_TAB } from '../../../../../constants/individualOpenModes';
import { FaPlus } from 'react-icons/fa';
import { HeaderButton } from '../../../../common/styles';
import { useRadarData } from '../../../../../hooks/useRadarData';
import useChartData from '../../../../TrendRadarChart/hooks/useChartData';
import useSearch from '../../../../TrendRadarChart/hooks/useSearch';
import useTooltip from '../../../../TrendRadarChart/hooks/useTooltip';
import useZoomPan from '../../../../TrendRadarChart/hooks/useZoomPan';
import { TREE_CHART_MODES, COLORS, APPROVAL_STAGES } from '../../../../../constants/radar';
import TreeChart from '../../../../TrendRadarChart/components/TreeChart';
import ZoomControls from '../../../../TrendRadarChart/components/ZoomControls';
import { FormContainer, FormSection, SectionTitle } from '../../styles';
import { ChartPanel } from './styles';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import SkillHistoryModal from '../../../../TrendRadarChart/components/TreeChart/components/SkillHistoryModal';
import EmptyState from '../../../../common/EmptyState';
import { SEGMENT_TYPE } from '../../../../../constants/radar';
import { competencyTemplatesApi } from '../../../../../services/api/competencyTemplatesApi';
import { SKILL_TYPES } from '../../../../../constants/skillTypes';

const Competencies = ({ employeeData, isViewMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // State hooks - must be at the top
  const [employeeHierarchyData, setEmployeeHierarchyData] = useState(null);
  const [expandedPath, setExpandedPath] = useState([]);
  const [isTreeExpanded, setIsTreeExpanded] = useState(true);
  const [initialCheckedLeafs, setInitialCheckedLeafs] = useState(new Set());
  const [selectedSkillHistory, setSelectedSkillHistory] = useState(null);
  const [showCompetencyLevels, setShowCompetencyLevels] = useState(true);
  const [showSkillHistory, setShowSkillHistory] = useState(true);
  const [competencyLevels, setCompetencyLevels] = useState([]);

  // Fetch competency templates for skill levels
  useEffect(() => {
    const fetchCompetencyTemplates = async () => {
      try {
        const templates = await competencyTemplatesApi.getTemplatesByType(SKILL_TYPES.TECHNICAL);
        if (templates && templates.length > 0 && templates[0].levels) {
          const transformedLevels = templates[0].levels.map(level => ({
            level: level.level,
            displayName: level.name,
            color: level.color,
            levelId: level.id
          }));
          setCompetencyLevels(transformedLevels);
        }
      } catch (error) {
        console.error('Failed to fetch competency templates:', error);
      }
    };
    fetchCompetencyTemplates();
  }, []);

  // Data fetching hook
  const { data: radarData, loading: radarLoading, error: radarError } = useRadarData({
    skip: !employeeData?.employeeLeafSegments?.length
  });

  // Empty placeholder for leaf assignment check
  const isLeafAssigned = useCallback((leafId) => {
    if (!employeeData?.employeeLeafSegments) {
      return false;
    }
    return employeeData.employeeLeafSegments.some(
      (leafSegment) => leafSegment.leafSegment?.segmentId === leafId
    );
  }, [employeeData]);

  // Get filtered data
  const getFilteredData = useCallback(() => {
    let filteredData = employeeHierarchyData;
    if (!filteredData) return null;

    // Apply selection filter
    const filterBySelection = (node) => {
      if (!node) return null;

      if (!node.subcategories || node.subcategories.length === 0) {
        return initialCheckedLeafs.has(node.leafSegmentId) ? node : null;
      }

      const filteredSubcategories = (node.subcategories || [])
        .map(filterBySelection)
        .filter(Boolean);

      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    const selectionFiltered = filterBySelection(filteredData);
    // If no nodes match selection criteria, return empty root node
    return selectionFiltered || {
      ...filteredData,
      subcategories: []
    };
  }, [employeeHierarchyData, initialCheckedLeafs]);

  // Get the filtered data using useMemo
  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  // Set initial radar data
  React.useEffect(() => {
    if (radarData) {
      // Add skill levels to leaf nodes
      const enrichDataWithSkillLevels = (data) => {
        if (!data) return null;

        const enrichNode = (node) => {
          if (node.segmentType === SEGMENT_TYPE.LEAF) {
            // Find the employee leaf segment for this node
            const employeeLeafSegment = employeeData?.employeeLeafSegments?.find(
              segment => segment.leafSegment?.id === node.leafSegmentId
            );

            // Add competencyLevelId, skillHistory and currentSkill properties if the leaf is assigned to the employee
            return {
              ...node,
              competencyLevelId: employeeLeafSegment?.competencyLevelId || employeeLeafSegment?.competencyLevel?.id || null,
              skillHistory: employeeLeafSegment?.skillHistory || null,
              currentSkill: employeeLeafSegment?.currentSkill || null
            };
          }

          // If it's not a leaf node, process its subcategories
          if (node.subcategories?.length > 0) {
            return {
              ...node,
              subcategories: node.subcategories.map(enrichNode)
            };
          }

          return node;
        };

        return enrichNode(data);
      };

      const enrichedData = enrichDataWithSkillLevels(radarData);

      // Inject custom segments into the tree under "Custom Technologies" category
      const injectCustomSegments = (treeData) => {
        // Find all custom segments (where leafSegment is null but customSegment exists)
        const customSegments = employeeData?.employeeLeafSegments?.filter(
          s => s.leafSegment == null && s.customSegment != null
        ) || [];

        if (customSegments.length === 0) {
          return treeData;
        }

        // Create "Custom Technologies" category node (first-level INTERNAL node)
        const customTechNode = {
          id: 'custom-technologies',
          name: 'Custom Technologies',
          segmentType: SEGMENT_TYPE.INTERNAL,
          internalSegmentId: 'custom-technologies',
          color: '#9C27B0',  // Purple color for custom category
          subcategories: [],
          segmentProcesses: []
        };

        // Create leaf nodes for each custom segment, sorted by name
        const sortedCustomSegments = [...customSegments].sort((a, b) =>
          (a.customSegment.name || '').localeCompare(b.customSegment.name || '')
        );

        sortedCustomSegments.forEach((segment) => {
          const customLeafNode = {
            id: segment.customSegment.id,
            name: segment.customSegment.name,
            segmentType: SEGMENT_TYPE.LEAF,
            leafSegmentId: segment.customSegment.id,
            isCustomSegment: true,  // Flag to identify custom segments
            tags: [],
            approvalStage: APPROVAL_STAGES.HOLD,
            projects: [],
            subcategories: [],
            // Include competency level and history for display (controlled by filters)
            competencyLevelId: segment.competencyLevelId || segment.competencyLevel?.id || null,
            skillHistory: segment.skillHistory || null,
            currentSkill: segment.currentSkill || null
          };

          customTechNode.subcategories.push(customLeafNode);
        });

        // Inject as first-level category in original tree
        return {
          ...treeData,
          subcategories: [...(treeData.subcategories || []), customTechNode]
        };
      };

      const dataWithCustomSegments = injectCustomSegments(enrichedData);
      setEmployeeHierarchyData(dataWithCustomSegments);

      // Set initial checked leafs based on employee's assigned segments (including custom segments)
      const radarLeafs = employeeData?.employeeLeafSegments
        ?.filter((leafSegment) => leafSegment.leafSegment != null)
        ?.map((leafSegment) => leafSegment.leafSegment.id) || [];

      const customLeafs = employeeData?.employeeLeafSegments
        ?.filter((s) => s.leafSegment == null && s.customSegment != null)
        ?.map((s) => s.customSegment.id) || [];

      const initialLeafs = new Set([...radarLeafs, ...customLeafs]);
      setInitialCheckedLeafs(initialLeafs);
    }
  }, [radarData, employeeData]);

  // Hooks for chart functionality
  const {
    selectedSubcategory,
    handleSelection,
    handleBack,
  } = useChartData(employeeHierarchyData || {}, TREE_CHART_MODES.TREE);

  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch
  } = useSearch(filteredData || {}, selectedSubcategory);

  const {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  } = useTooltip();

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getZoomFactor,
  } = useZoomPan();

  const handleSkillHistoryClick = (nodeData) => {
    setSelectedSkillHistory(nodeData);
  };

  const handleManageTechnicalSkills = () => {
    if (!employeeData) return;

    navigate(`/employee-radar/${employeeData.id}`, {
      state: {
        user: {
          id: employeeData.id,
          username: employeeData.email,
          name: employeeData.fullName
        },
        // ADR-109 §3 — come back to this Individual (Technology Radar tab) with the
        // state it was opened with, so its "Back to <report>" still restores the report.
        individualReturn: buildIndividualReturn({
          pathname: location.pathname,
          search: location.search,
          tab: INDIVIDUAL_TECHNOLOGY_RADAR_TAB,
          state: location.state,
        }),
      }
    });
  };

  // Render content based on different states
  const renderContent = () => {
    // Early return if no leaf segments
    if (!employeeData?.employeeLeafSegments?.length) {
      return (
        <ChartPanel>
          <EmptyState
            title="No Competencies Found"
            description="No competencies defined for this individual yet."
          />
        </ChartPanel>
      );
    }

    if (radarLoading) {
      return <LoadingSpinner message="Loading competencies data..." />;
    }

    if (radarError) {
      return (
        <ChartPanel>
          <EmptyState
            title="Error Loading Competencies"
            description="Failed to load competencies data. Please try again later."
          />
        </ChartPanel>
      );
    }

    return (
      <ChartPanel>
        <div style={{ display: 'flex', gap: '1rem', height: '100%' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <TreeChart
              data={filteredData}
              selectedSubcategory={selectedSubcategory}
              projectData={null}
              hoveredItem={hoveredItem}
              onHover={handleHover}
              onMouseOut={handleMouseOut}
              onSelection={handleSelection}
              onBack={handleBack}
              zoomFactor={treeZoomFactor}
              configMode={TREE_CHART_MODES.EMPLOYEE_READ_ONLY}
              expandedPath={expandedPath}
              setExpandedPath={setExpandedPath}
              isLeafAssigned={isLeafAssigned}
              initialCheckedLeafs={initialCheckedLeafs}
              isTreeExpanded={isTreeExpanded}
              onTreeExpansionToggle={setIsTreeExpanded}
              onSkillHistoryClick={handleSkillHistoryClick}
              showCompetencyLevels={showCompetencyLevels}
              showSkillHistory={showSkillHistory}
              skillLevels={competencyLevels}
            />
            <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '1rem' }}>
              <ZoomControls
                zoomFactor={treeZoomFactor}
                onZoomIn={(isMax) => handleZoomIn(TREE_CHART_MODES.TREE, isMax)}
                onZoomOut={() => handleZoomOut(TREE_CHART_MODES.TREE)}
                onReset={() => handleResetPosition(TREE_CHART_MODES.TREE)}
              />
            </div>
          </div>
        </div>
      </ChartPanel>
    );
  };

  return (
    <FormContainer>
      <FormSection>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCompetencyLevels}
                  onChange={(e) => setShowCompetencyLevels(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2" sx={{ color: COLORS.PRIMARY }}>Show Competency Levels</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSkillHistory}
                  onChange={(e) => setShowSkillHistory(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2" sx={{ color: COLORS.PRIMARY }}>Show History</Typography>}
            />
          </Box>
          {!isViewMode && (
            <HeaderButton onClick={handleManageTechnicalSkills}>
              <FaPlus /> Technology Configuration
            </HeaderButton>
          )}
        </Box>
        {renderContent()}
      </FormSection>

      <SkillHistoryModal
        isOpen={!!selectedSkillHistory}
        onClose={() => setSelectedSkillHistory(null)}
        skillData={selectedSkillHistory}
        skillLevels={competencyLevels}
      />
    </FormContainer>
  );
};

export default Competencies; 
